import * as React from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';

const DialogStyled = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(2),
    },

    '& .MuiPaper-root.MuiDialog-paper': {
        width: '100%',
    },
}));
const DialogTitleStyled = styled(DialogTitle)(({ theme }) => ({
    '&.MuiDialogTitle-root': {
        color: theme.palette.text.primary,
        leadingTrim: 'both',
        textEdge: 'cap',
        // textTransform: 'capitalize',
        fontSize: theme.typography.customFontSize.xxxLarge,
        fontWeight: theme.typography.fontWeightSemiBold,
        lineHeight: theme.typography.customFontSize.xxxLarge /* 100% */,
        marginRight: '3rem',

        [theme.breakpoints.down('sm')]: {
            fontSize: theme.typography.customFontSize.large,
        },
    },
}));
const DialogSubTitleStyled = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.tertiary,
    fontSize: theme.typography.customFontSize.medium,
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: theme.typography.customFontSize.large /* 100% */,
    padding: `${theme.typography.generic.none} ${theme.typography.generic.none}`,

    [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.customFontSize.small,
    },
}));

const DialogActionsStyled = styled(DialogActions)(({ theme }) => ({
    '& .MuiButtonBase-root.MuiButton-root': {
        padding: `${theme.typography.generic.xSmall} ${theme.typography.generic.xxxxxLarge}`,
    },
}));

const DEFAULT_CONTENT = (
    <>
        <Typography gutterBottom>
            Cras mattis consectetur purus sit amet fermentum. Cras justo odio, dapibus ac facilisis in, egestas eget quam. Morbi leo risus, porta ac consectetur ac, vestibulum at eros.
        </Typography>
        <Typography gutterBottom>Praesent commodo cursus magna, vel scelerisque nisl consectetur et. Vivamus sagittis lacus vel augue laoreet rutrum faucibus dolor auctor.</Typography>
        <Typography gutterBottom>
            Aenean lacinia bibendum nulla sed consectetur. Praesent commodo cursus magna, vel scelerisque nisl consectetur et. Donec sed odio dui. Donec ullamcorper nulla non metus auctor fringilla.
        </Typography>
    </>
);

function Modal({
    modalOpen = false,
    handleModalClose = null,
    handleModalOnSuccess = null,
    title = 'Modal title',
    subTitle = '',
    children = DEFAULT_CONTENT,
    hideActions = false,
    SubmitBtnUI = null,
    CancelBtnUI = null,
    submitBtnProps = null,
    iconButtonSx={},
    isBackdropCloseOn = false,
    ...props
}) {
    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    return (
        <React.Fragment>
            {/* {openModalBtnUI ? (
                openModalBtnUI
            ) : (
                <Button variant="outlined" onClick={handleClickModalOpen || handleClickOpen}>
                    Open dialog
                </Button>
            )} */}
            <DialogStyled
                onClose={isBackdropCloseOn ? handleModalClose || handleClose : null} //remove click on backdrowp then modal remove functinality
                aria-labelledby="customized-dialog-title"
                open={modalOpen}
                {...props}
            >
                {title && (
                    <DialogTitleStyled sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                        {title}
                    </DialogTitleStyled>
                )}

                {subTitle && (
                    <DialogSubTitleStyled sx={{ m: 0, p: 2, marginTop: -3 }} id="customized-dialog-subtitle">
                        {subTitle}
                    </DialogSubTitleStyled>
                )}
                <IconButton
                    aria-label="close"
                    onClick={handleModalClose || handleClose}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                        ...iconButtonSx,

                        // '& .MuiSvgIcon-root': {
                        //     fontSize: {
                        //         xs: theme.typography.customFontSize.xLarge,
                        //         sm: theme.typography.customFontSize.xxxLarge,
                        //         // md: theme.typography.customFontSize.xLarge,
                        //         // lg: theme.typography.customFontSize.xLarge,
                        //     },
                        // },
                    })}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent dividers>{children}</DialogContent>
                {!hideActions && (
                    <DialogActionsStyled>
                        {CancelBtnUI ?? (
                            <Button type="button" onClick={handleModalClose || handleClose}>
                                Cancel
                            </Button>
                        )}
                        {SubmitBtnUI ?? (
                            <Button type="button" variant="contained" onClick={handleModalOnSuccess || null} {...submitBtnProps}>
                                {submitBtnProps?.children ? submitBtnProps?.children : 'Add task'}
                            </Button>
                        )}
                    </DialogActionsStyled>
                )}
            </DialogStyled>
        </React.Fragment>
    );
}

/**
 * Modal
 *
 * A customizable modal dialog component that displays content and allows for user interactions.
 *
 * This component includes a title, optional subtitle, content area, and action buttons
 * (e.g., Cancel and Submit). It supports custom styles for both the button and the dropdown menu,
 * and can display default content if no children are provided.
 *
 * @param {boolean} modalOpen - Controls the visibility of the modal (true = open, false = closed).
 * @param {function} handleModalClose - Callback function to handle modal close events.
 * @param {function} handleModalOnSuccess - Callback function to handle successful actions
 *                                           when the submit button is clicked.
 * @param {string} title - Title of the modal, displayed prominently at the top.
 * @param {string} subTitle - Optional subtitle for additional context beneath the title.
 * @param {JSX.Element} children - Content to be displayed within the modal. Defaults to predefined content
 *                                  if not provided.
 * @param {boolean} hideActions - If true, hides the action buttons at the bottom of the modal.
 * @param {JSX.Element} SubmitBtnUI - Optional custom UI for the submit button.
 * @param {JSX.Element} CancelBtnUI - Optional custom UI for the cancel button.
 * @param {object} submitBtnProps - Additional props to pass to the submit button.
 * @param {...object} props - Any additional props to be passed to the Dialog component.
 *
 * @returns {JSX.Element} - A modal dialog with customizable content and actions.
 */
export default Modal;
